/**
 * Navigation utilities for mapping between menu IDs and URL paths.
 * Used during the React Router migration to bridge the old menu-based
 * navigation system with the new URL-based routing.
 */

/** Where the External shell's persistent "Submit a sample" action lands: Home's pathways section. */
export const SUBMIT_SAMPLE_PATH = '/portal/home#submit';

const menuIdToPathMap: Record<string, string> = {
  'dashboard': '/dashboard',
  'agent-portal': '/portal',
  'customer-home': '/portal/home',
  // New External IA (plan §4) — explicit entries so the `/portal*` prefix fallback below never
  // mistitles the AppBar as "My Reports".
  'portal-samples': '/portal/samples',
  'portal-reports': '/portal/reports',
  'portal-farms': '/portal/farms',
  'portal-profile': '/portal/profile',
  'portal-smart-sampling': '/portal/smart-sampling',
  'portal-welcome': '/portal/welcome',
  'portal-buy': '/portal/buy',
  'portal-buy-return': '/portal/buy/return',
  'analyse-results': '/portal/analyse',
  'onboarding': '/portal/onboarding',
  'my-farmers': '/portal/farmers',
  'intake-submit': '/portal/intake',
  'my-submissions': '/portal/submissions',
  'file-upload': '/portal/files',
  'uploaded-files': '/uploaded-files',
  'batches': '/batches',
  'workflow-dashboard': '/workflow',
  'consolidated-invoicing': '/finance/consolidated',
  'data-import': '/import',
  'legacy-calculator': '/legacy-calculator',
  'entity-search': '/farm-workspace',
  'farmers': '/farmers',
  'agents': '/agents',
  'labs': '/labs',
  'farms': '/farms',
  'farm-plots': '/farm-plots',

  'crop-types': '/crop-types',
  'crop-sub-types': '/crop-sub-types',
  'sample-types': '/sample-types',
  'sage-sample-type-mappings': '/sample-types',
  'ratio-configurations': '/ratio-configurations',
  'element-rankings': '/element-rankings',
  'hybrid-norms-view': '/hybrid-norms-view',
  'hybrid-norms-calculator': '/hybrid-norms',
  'hybrid-norms': '/hybrid-norms',
  'nutrient-norms': '/norms',
  'norms': '/norms',
  'ai-prompt': '/settings/ai-prompt',
  'compression-training': '/settings/compression-training',
  'dris-calculation-settings': '/settings/dris-calculation',
  'element-breakpoints': '/settings/element-breakpoints',
  'sage-settings': '/settings/sage',
  'claude-settings': '/settings/claude',
  'calendar-settings': '/settings/calendar',
  'communication': '/communication',
  'fertilizer-programs': '/fertilizer-programs',
  'product-factors': '/settings/product-factors',
  'recalculate-samples': '/settings/recalculate-samples',
  'user-management': '/user-management',
  'role-management': '/admin/roles',
  'workflow-permissions': '/admin/workflow-permissions',
  'menu-permissions': '/admin/menu-permissions',
  'access-grants': '/admin/access-grants',
  'report-audit': '/admin/report-audit',
  'duplicate-scan': '/admin/duplicate-scan',
  'notifications': '/notifications',
  'profile': '/profile',

  // Partner Portal (Partner-role users)
  'partner-portal': '/partner',
  'partner-submit': '/partner/submit',
  'partner-entities': '/partner/entities',
  'partner-results': '/partner/results',
  'partner-api-keys': '/partner/api-keys',
  'partner-communication': '/partner/communication',
};

/**
 * Convert a menu ID to a URL path.
 * Falls back to /dashboard if the menu ID is not found.
 */
export function menuIdToPath(menuId: string): string {
  return menuIdToPathMap[menuId] || '/dashboard';
}

/**
 * Reverse map: URL path -> canonical menu ID.
 * For paths with multiple menu ID aliases, uses the canonical ID from menuItems.tsx.
 */
const pathToMenuIdMap: Record<string, string> = {};
// Build from menuIdToPathMap, but let canonical IDs win
// First pass: add all entries
for (const [menuId, path] of Object.entries(menuIdToPathMap)) {
  pathToMenuIdMap[path] = menuId;
}
// Second pass: override with canonical IDs (these are the ones used in menuItems.tsx)
const canonicalOverrides: Record<string, string> = {
  '/hybrid-norms': 'hybrid-norms-calculator',
  '/norms': 'nutrient-norms',
  '/sample-types': 'sample-types',
  // Superseded v1 hub URL — redirects to /farm-workspace; keeps the sidebar selection during the redirect.
  '/entity-search': 'entity-search',
};
Object.assign(pathToMenuIdMap, canonicalOverrides);

/**
 * Convert a URL pathname to the corresponding menu ID.
 * Uses an explicit reverse map for O(1) lookups, falling back to
 * prefix matching for dynamic routes.
 */
export function pathToMenuId(pathname: string): string {
  // Direct lookup (O(1))
  const directMatch = pathToMenuIdMap[pathname];
  if (directMatch) return directMatch;

  // Prefix matching for dynamic routes
  // Farm Workspace hub: /farm-workspace/farmers/:id[/farms/:id][/:tab] (+ the redirecting v1 /entity-search/*)
  if (pathname.startsWith('/farm-workspace/') || pathname.startsWith('/entity-search/')) return 'entity-search';
  // SS External self-service — must precede the generic /portal fallback so /portal/smart-sampling,
  // /portal/smart-sampling/new and /portal/smart-sampling/:id resolve to their own menu id (and title).
  if (pathname.startsWith('/portal/smart-sampling')) return 'portal-smart-sampling';
  if (pathname.startsWith('/portal')) return 'agent-portal';
  if (pathname.startsWith('/partner/results/')) return 'partner-results-workspace';
  if (pathname.startsWith('/partner')) return 'partner-portal';
  if (pathname.startsWith('/workflow/')) return 'workflow-dashboard';
  if (pathname.startsWith('/finance/')) return 'consolidated-invoicing';
  if (pathname.startsWith('/admin/')) {
    // Try to find an exact admin sub-path
    const adminMatch = pathToMenuIdMap[pathname];
    if (adminMatch) return adminMatch;
  }
  if (pathname.startsWith('/settings/')) {
    // Try to find an exact settings sub-path
    const settingsMatch = pathToMenuIdMap[pathname];
    if (settingsMatch) return settingsMatch;
  }

  return 'dashboard';
}
